import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Zap, Target, RefreshCw } from 'lucide-react';
interface SlideStrategyProps {
  onContinue: () => void;
}
const PILLARS = [{
  step: '01',
  title: 'Leverage existing assets',
  desc: 'Approved materials become interactive Snaks — no new content creation.',
  icon: Layers
}, {
  step: '02',
  title: 'Rapid local activation',
  desc: 'Centrally built, locally activated. Live in days, not months.',
  icon: Zap
}, {
  step: '03',
  title: 'Behavioural data capture',
  desc: 'Real HCP engagement signals, not vanity metrics.',
  icon: Target
}, {
  step: '04',
  title: 'Continuous optimization',
  desc: 'Every interaction sharpens the next campaign — and every market learns.',
  icon: RefreshCw
}] as const;
interface PillarCardProps {
  pillar: (typeof PILLARS)[number];
  visible: boolean;
  className?: string;
}
function PillarCard({
  pillar,
  visible,
  className = ''
}: PillarCardProps) {
  const Icon = pillar.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: visible ? 1 : 0,
    y: visible ? 0 : 14
  }} transition={{
    duration: 0.5
  }} className={`w-full max-w-[300px] bg-navy-900 border border-navy-600 rounded-2xl p-5 shadow-[0_8px_30px_rgba(15,23,42,0.06)] ${className}`} data-id="element-1177" data-mp="tuxc4y5d6hxi">
      <div className="flex items-center gap-3 mb-2" data-id="element-1178" data-mp="b0id58j1yp9o">
        <div className="w-9 h-9 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center shrink-0" data-id="element-1179" data-mp="f3ewx2kbwrf7">
          <Icon className="w-4 h-4" data-id="element-1180" data-mp="14p4i08gffx6" />
        </div>
        <div className="flex items-center gap-2" data-id="element-1181" data-mp="16037ln1lxhf">
          <span className="text-teal-400 font-bold text-xs" data-id="element-1182" data-mp="641nub04a4v0">{pillar.step}</span>
          <h3 className="text-white font-semibold text-sm md:text-base leading-tight" data-id="element-1183" data-mp="1vhexmfr00l3i">
            {pillar.title}
          </h3>
        </div>
      </div>
      <p className="text-white/60 text-sm leading-relaxed" data-id="element-1184" data-mp="20g89k3qn041k">{pillar.desc}</p>
    </motion.div>;
}
export function SlideStrategy({
  onContinue
}: SlideStrategyProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 800, 1300, 1800, 2400].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-16 md:py-12 max-w-7xl mx-auto" data-id="element-1185" data-mp="1k4oe8vypdc7x">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-3 shrink-0" data-id="element-1186" data-mp="2hseu1i1t2pf7">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1187" data-mp="231froorkcx42">
          The Operating Model
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1188" data-mp="30h3e61sgsqpx">
          A loop, not a project
        </h2>
        <p className="text-lg text-white/60" data-id="element-1189" data-mp="2vp24mnmole3l">
          Each campaign cycle feeds the next — across every brand and market.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex items-center justify-center mt-6" data-id="element-1190" data-mp="380cn5tx26neg">
        <div className="relative w-full max-w-[1060px] h-full flex items-center" data-id="element-1191" data-mp="382fb88eni6gj">
          {/* Rotating loop — a true circle, centred on the grid */}
          <div className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none z-0" aria-hidden="true" data-id="element-1192" data-mp="3hysfg07to2pj">
            <div className="relative h-[64%] aspect-square" data-id="element-1193" data-mp="3qz72p54cjrvo">
              <div className="absolute inset-0 rounded-full border border-navy-600" data-id="element-1194" data-mp="3qxnnlhuffmvh" />
              <motion.div className="absolute inset-0" initial={{
              rotate: 0
            }} animate={{
              rotate: stage >= 1 ? 360 : 0
            }} transition={{
              repeat: Infinity,
              duration: 9,
              ease: 'linear'
            }} data-id="element-1195" data-mp="3vr8aoeuhewfw">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-teal-400 shadow-[0_0_14px_rgba(45,212,191,0.9)]" data-id="element-1196" data-mp="1b7vt2hl8pow5" />
              </motion.div>
            </div>
          </div>

          {/* 3 × 3 grid — the hub sits in the exact centre cell */}
          <div className="relative z-10 w-full grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4 items-center justify-items-center" data-id="element-1197" data-mp="1g185zugk4792">
            <div className="hidden md:block" data-id="element-1198" data-mp="sc5s8xp1myju" />
            <PillarCard pillar={PILLARS[0]} visible={stage >= 1} className="order-1 md:order-none" data-id="element-1199" data-mp="nc16ot9a3dmr" />
            <div className="hidden md:block" data-id="element-1200" data-mp="118o8b9c6erz0" />

            <PillarCard pillar={PILLARS[3]} visible={stage >= 4} className="order-4 md:order-none" data-id="element-1201" data-mp="x8cnnwjtcrnj" />

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: stage >= 5 ? 1 : 0,
            scale: stage >= 5 ? 1 : 0.9
          }} className="w-full max-w-[240px] order-5 md:order-none" data-id="element-1202" data-mp="28okuia6a2p50">
              <div className="bg-navy-800 border border-teal-400/40 rounded-2xl px-5 py-4 text-center" data-id="element-1203" data-mp="26apbvmy9by5c">
                <p className="text-teal-400 font-semibold text-[11px] uppercase tracking-widest" data-id="element-1204" data-mp="2ba1z48t1j78g">
                  Compounding
                </p>
                <p className="text-white font-serif text-lg mt-1 leading-snug" data-id="element-1205" data-mp="2z0xq53po9yen">
                  Every cycle gets sharper
                </p>
              </div>
            </motion.div>

            <PillarCard pillar={PILLARS[1]} visible={stage >= 2} className="order-2 md:order-none" data-id="element-1206" data-mp="2p6pb9itke3yy" />

            <div className="hidden md:block" data-id="element-1207" data-mp="3hx7dyiqdm3bu" />
            <PillarCard pillar={PILLARS[2]} visible={stage >= 3} className="order-3 md:order-none" data-id="element-1208" data-mp="3hvcpwvjt5v3j" />
            <div className="hidden md:block" data-id="element-1209" data-mp="dp8ep6x7obc" />
          </div>
        </div>
      </div>
    </div>;
}